import { GlobalSettings } from '@services/types';

export type GlobalSettingFormItem = GlobalSettingForm;
type GlobalSettingForm = {
  label: string;
  tooltip?: string;
  description?: {
    header: string;
    rules: string[];
  };
  values: {
    field: keyof GlobalSettings;
    label: string;
    type: 'string' | 'number' | 'boolean' | 'url';
    deprecated?: string;
    value: {
      min?: number;
      max?: number;
      default: string | number | boolean;
    };
    description: {
      header: string;
      rules: string[];
    };
  }[];
};

interface GlobalConfig {
  labels: GlobalSettingForm;
  jobs: GlobalSettingForm;
  other: GlobalSettingForm;
}

export const formConfig: GlobalConfig = {
  labels: {
    label: 'Labels',
    tooltip: 'This Section Controls Label Names. Only User Interface is impacted.',
    values: [
      {
        field: 'loaderLabelName',
        label: 'Loader Label Name',
        type: 'string',
        value: {
          default: 'LOADER',
        },
        description: {
          header: 'The name to display in the GUI for the LOADER packages.',
          rules: ['Value Type: String', 'Mandatory', 'Default: LOADER'],
        },
      },
      {
        field: 'firmwareLabelName',
        label: 'FW Label Name',
        type: 'string',
        value: {
          default: 'FW',
        },
        description: {
          header: 'The name to display in the GUI for the FW packages.',
          rules: ['Value Type: String', 'Mandatory', 'Default: FW'],
        },
      },
      {
        field: 'uiLabelName',
        label: 'UI Label Name',
        type: 'string',
        value: {
          default: 'UI',
        },
        description: {
          header: 'The the name to display in the GUI for the UI/EDA packages.',
          rules: ['Value Type: String', 'Mandatory', 'Default: UI'],
        },
      },
      {
        field: 'customFirmware1LabelName',
        label: 'CFW1 Label Name',
        type: 'string',
        value: {
          default: 'UI',
        },
        description: {
          header: 'Custom Firmware Name 1. Leave empty to disable.',
          rules: ['Value Type: String', 'The the name to display in the GUI for the CFW packages', 'Default: CFW1'],
        },
      },
      {
        field: 'customFirmware2LabelName',
        label: 'CFW2 Label Name',
        type: 'string',
        value: {
          default: 'UI',
        },
        description: {
          header: 'Custom Firmware Name 2. Leave empty to disable.',
          rules: ['Value Type: String', 'The the name to display in the GUI for the CFW packages', 'Default: CFW2'],
        },
      },
      {
        field: 'customFirmware3LabelName',
        label: 'CFW3 Label Name',
        type: 'string',
        value: {
          default: 'UI',
        },
        description: {
          header: 'Custom Firmware Name 3. Leave empty to disable.',
          rules: ['Value Type: String', 'The the name to display in the GUI for the CFW packages', 'Default: CFW3'],
        },
      },
    ],
  },
  jobs: {
    label: 'Jobs',
    description: {
      header: 'In this section job specific parameters can be configured.',
      rules: [
        'This is a trade-off between performance and usability caused by DB service limitations (f.e. AWS RDS)',
        'Warning1: do not change these parameters without contacting application support',
        'Warning2: in case of cloud hosting and large number of devices it is not recommended to change these values',
      ],
    },
    values: [
      {
        field: 'jobExportBatchSize',
        label: 'Export Batch Size',
        type: 'number',
        value: {
          default: 1000,
          min: 10,
          max: 5000,
        },
        description: {
          header: 'Number of devices in a batch in case of export job.',
          rules: ['Value Type: Integer', 'Value Range: [10..5000], Mandatory', 'Default: 1000'],
        },
      },
      {
        field: 'jobExportBatchWaitDuration',
        label: 'Export Wait Time Milliseconds',
        type: 'number',
        value: {
          default: 1000,
          min: 0,
          max: 10000,
        },
        description: {
          header: 'Wait time in milliseconds between each batch in case of export job.',
          rules: ['Value Type: Integer', 'Value Range: [0..10000], Mandatory', 'Default: 1000'],
        },
      },
      {
        field: 'jobBatchWaitDuration',
        label: 'Wait Time Milliseconds',
        type: 'number',
        value: {
          default: 1000,
          min: 0,
          max: 10000,
        },
        description: {
          header: 'Wait time in milliseconds between each batch.',
          rules: ['Value Type: Integer', 'Value Range: [0..10000], Mandatory', 'Default: 1000'],
        },
      },
      {
        field: 'jobMaxQueued',
        label: 'Max Queued Jobs',
        type: 'number',
        value: {
          default: 30,
          min: 1,
          max: 100,
        },
        description: {
          header: 'Max pending jobs. This is a soft limit to the number or jobs that can be queued.',
          rules: ['Value Type: Integer', 'Value Range: [1..100], Mandatory', 'Default: 30'],
        },
      },
    ],
  },
  other: {
    label: 'Other',
    values: [
      {
        field: 'authLockMessage',
        label: 'Auth Lock Message',
        type: 'string',
        value: {
          default: 'If you think an attacker is trying to access your account please contact your administrator.',
        },
        description: {
          header: 'Define here message sent to user when account is locked after invalid connection attempt.',
          rules: [
            'Value Type: String',
            'Value Range: Max 500 character',
            'Default: If you think an attacker is trying to access your account please contact your administrator.',
          ],
        },
      },
      {
        field: 'enableDeviceHistory',
        label: 'Enable Device History',
        type: 'boolean',
        deprecated: 'This setting is going to be removed in the next release.',
        value: {
          default: true,
        },
        description: {
          header: 'Enable check box "Log History" in UI device edit screen.',
          rules: ['Value Type: Boolean', 'Default: true'],
        },
      },
      {
        field: 'maxDeviceHistory',
        label: 'Max Device History Records',
        type: 'number',
        value: {
          default: 30,
          min: 1,
          max: 100,
        },
        description: {
          header: 'Max device version history size.',
          rules: ['Value Type: Integer', 'Value Range: [1..100], Mandatory', 'Default: 30'],
        },
      },
      {
        field: 'appUrl',
        label: 'App Url',
        type: 'url',
        value: {
          default: '',
        },
        description: {
          header: 'Admin Server (this server) url.',
          rules: ['Value Type: Integer', 'Default empty'],
        },
      },
      {
        field: 'enableStbCommands',
        label: 'Enable stb commands',
        type: 'boolean',
        value: {
          default: false,
        },
        description: {
          header: 'Enable Stb Commands.',
          rules: [
            'Enable flag for device attributes Trace Enabled, Full Reset, Light Reset',
            'Value Type: Boolean',
            'Default: false',
          ],
        },
      },
      {
        field: 'enableIpGroup',
        label: 'Enable Ip Group',
        type: 'boolean',
        value: {
          default: false,
        },
        description: {
          header: 'Enable Ip Group.',
          rules: [
            'An IP groups is a STB group with IP ranges attached. Devices are moved automatically to the IP groups if the device source IP match the group IP ranges.',
            'Value Type: Boolean',
            'Default: false',
          ],
        },
      },
      {
        field: 'enableTags',
        label: 'Enable Tags',
        type: 'boolean',
        value: {
          default: false,
        },
        description: {
          header: 'Enable Tags',
          rules: ['Enable Tags', 'Value Type: Boolean', 'Default: false'],
        },
      },
      {
        field: 'enableRescue',
        label: 'Enable Rescue',
        type: 'boolean',
        value: {
          default: false,
        },
        description: {
          header: 'Enable Rescue.',
          rules: ['Enable/Disable Rescue Page', 'Value Type: Boolean', 'Default: false'],
        },
      },
      {
        field: 'enableDisablingExtendedVersioning',
        label: 'Disable Extended Versioning',
        type: 'boolean',
        value: {
          default: false,
        },
        description: {
          header: 'Disable Extended Versioning.',
          rules: ['Disable Extended Versioning', 'Value Type: Boolean', 'Default: false'],
        },
      },
      {
        field: 'enableCompareFwVersions',
        label: 'Enable Compare Versions',
        type: 'boolean',
        value: {
          default: false,
        },
        description: {
          header: 'Enable Compare Versions.',
          rules: ['Enable/Disable Compare Versions', 'Value Type: Boolean', 'Default: false'],
        },
      },
      {
        field: 'enableChainingRule',
        label: 'Enable Chaining Rule',
        type: 'boolean',
        value: {
          default: false,
        },
        description: {
          header: 'Enable Chaining Rule.',
          rules: ['Enable/Disable Chaining Rule.', 'Value Type: Boolean', 'Default: false'],
        },
      },
      {
        field: 'enableTimeWindow',
        label: 'Enable Time Window',
        type: 'boolean',
        value: {
          default: false,
        },
        description: {
          header: 'Enable Time Window.',
          rules: ['Enable/Disable Time Window.', 'Value Type: Boolean', 'Default: false'],
        },
      },
    ],
  },
};
